import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FileFilterModel,
  useCurrentAccount,
  useCurrentWorkspace,
  useFileFilter,
  WorkspaceType,
} from '@gripp/shared-logic';
import { Colors, FilesSearchInput, FilterType } from '@gripp/shared-ui';
import { useMemo, useState } from 'react';
import { StyleProp, StyleSheet, Text, View } from 'react-native';
import { FilterBar } from '../../shared/components/filter/filterBar';
import { ModalButton } from '../../shared/components/modalButton';
import { CreateFileDropdown } from './createFileDropdown';
import { EditFileMenu } from './editFileMenu';
import { FileAssetsForm } from './FileAssetsForm';
import { FileAssignmentForm } from './fileAssignmentForm';
import { FilesList, SelectedFileRow } from './filesList';

export const Files = () => {
  const { data: fileFilter } = useFileFilter();
  const currentWorkspace = useCurrentWorkspace();
  const impersonatingWorkspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    const filter: FileFilterModel = fileFilter ?? new FileFilterModel();
    filter.workspaceId = currentWorkspace?.id;
    return filter.getGraphQLFilter();
  }, [fileFilter]);

  const [rowsSelected, setRowsSelected] = useState<SelectedFileRow[]>([]);
  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const showEditFileMenu = rowsSelected.length === 1;

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const CountButton = (data: any) => {
    const isProducerWorkspace =
      currentWorkspace?.type === WorkspaceType.Producer;

    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name={data.iconName}
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.count)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          if (isProducerWorkspace) {
            return (
              <FileAssetsForm
                fileId={data.fileId}
                title={data.name}
                showCheckboxColumn={showCheckboxColumn}
                onComplete={onComplete}
              />
            );
          }

          return (
            <FileAssignmentForm
              fileId={data.fileId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              // if user clicked addons count open addons tab first, else products
              initialTab={data.countType === 'addon' ? 'addons' : 'products'}
              onComplete={onComplete}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.filtersContainer}>
        <View style={styles.searchContainer}>
          <FilesSearchInput />
        </View>
        <FilterBar
          filterButtonTypes={[
            FilterType.FILECOLUMNS,
            FilterType.FILECATEGORIES,
          ]}
        />
        {showEditFileMenu ? (
          <EditFileMenu fileId={rowsSelectedIds[0]} label="Edit File" />
        ) : (
          <CreateFileDropdown />
        )}
      </View>
      <FilesList
        filter={filter}
        showCheckboxColumn={showCheckboxColumn}
        countButton={CountButton}
        isProducerWorkspace={currentWorkspace?.type === WorkspaceType.Producer}
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 44,
    marginBottom: 20,
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
});
